/**
 * Pure form state -> `manualOrders.create` input (Phase 104, D-17).
 *
 * No React, no I/O: the page calls these on every render (pieces, short
 * stock) and once on Create (the payload). The backend re-validates every
 * field (zod mirror of the SQL contract, then the SQL itself); this is the
 * first fence and the source of the inline errors.
 */
import { placementDpi } from '@threadheads/shared';
import { roundPlacement } from '@/components/platen-canvas/placement-format';
import { parseDateValue } from '@/lib/format-dates';
import { cardMethods, colourNameOf, groupLineCards } from './cards';
import { shortFor, stockFigure } from './stock-figure';
import type {
  ManualAddressState,
  ManualFormErrors,
  ManualLineIssue,
  ManualLineState,
  ManualPrintMethod,
  ManualOrderCreateInput,
  ManualOrderFormState,
  ManualOrderPayloadInput,
  ManualSpotState,
  ManualSpotUploadMeta,
  StockLineSize,
} from '../types';

// The placement helpers moved to the shared platen canvas (Phase 91); every
// Manual order import keeps reading them from here.
export {
  centreOffsetIn,
  formatInches,
  formatSignedInches,
  roundPlacement,
  samePlacement,
} from '@/components/platen-canvas/placement-format';

export const MANUAL_MAX_QUANTITY = 9999;

const FULL_WIDTH_DIGIT = /[０-９]/g;

/**
 * What a size box keeps from a keystroke or a paste: digits only (Chinese
 * IME full-width digits read as ASCII), no leading zeros, capped at 9999.
 */
export function sanitiseQuantity(raw: string): string {
  const ascii = raw.replace(FULL_WIDTH_DIGIT, (d) => String(d.charCodeAt(0) - 0xff10));
  const digits = ascii.replace(/\D/g, '');
  if (digits === '') return '';
  const n = Math.min(Number.parseInt(digits, 10), MANUAL_MAX_QUANTITY);
  return String(n);
}

export function parseQuantity(text: string): number {
  if (!text) return 0;
  const n = Number.parseInt(text, 10);
  return Number.isFinite(n) && n > 0 ? Math.min(n, MANUAL_MAX_QUANTITY) : 0;
}

/**
 * Pieces the typed quantity is over the size's stock figure (0 when covered):
 * own plus the 3PL back-up, or the 3PL pool on a supply alias. One rule with
 * the grid (`shortFor`, lib/stock-figure).
 */
export function shortBy(size: StockLineSize): number {
  return shortFor(size.quantity, stockFigure(size));
}

export function linePieces(line: ManualLineState): number {
  let total = 0;
  for (const size of line.sizes) total += parseQuantity(size.quantity);
  return total;
}

export function totalPieces(state: ManualOrderFormState): number {
  let total = 0;
  for (const line of state.lines) total += linePieces(line);
  return total;
}

const textOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

function buildAddress(address: ManualAddressState) {
  return {
    name: textOrNull(address.name),
    company: textOrNull(address.company),
    phone: textOrNull(address.phone),
    address1: address.address1.trim(),
    address2: textOrNull(address.address2),
    city: textOrNull(address.city),
    province: textOrNull(address.province),
    zip: textOrNull(address.zip),
    country: textOrNull(address.country),
    country_code: null,
  };
}

type PayloadLine = ManualOrderPayloadInput['lines'][number];

function buildSpots(spots: readonly ManualSpotState[]) {
  return spots.map((spot) => ({
    id: spot.id,
    side: spot.side,
    area: spot.area,
    upload_id: spot.uploadId,
    placement: spot.placement ? roundPlacement(spot.placement) : null,
  }));
}

/** The method sent for a line: null unless the floor still offers it (D-29). */
function lineMethod(line: ManualLineState): ManualPrintMethod | null {
  return line.printTechnology && line.printMethods.includes(line.printTechnology) ? line.printTechnology : null;
}

function buildLine(line: ManualLineState): PayloadLine {
  const common = {
    id: line.id,
    print_technology: lineMethod(line),
    note: textOrNull(line.note),
    mockup_upload_id: line.mockupUploadId,
    source_line_id: line.sourceLineId,
    spots: buildSpots(line.spots),
  };
  if (line.kind === 'stock') {
    return {
      ...common,
      kind: 'stock',
      // Every colour line of a card carries the card's group id (D-2, D-4); the server stores them as one group.
      group_id: line.groupId,
      garment_title: line.product?.title.trim() ?? '',
      // The colour's name as the floor reads it (the line keys it by its code).
      colour_label: colourNameOf(line),
      sizes: line.sizes
        .map((size) => ({ size_label: size.sizeLabel, sku: size.sku, quantity: parseQuantity(size.quantity) }))
        .filter((size) => size.quantity > 0),
    };
  }
  // Non-stock sizes carry no sku: the server composes each size's code.
  return {
    ...common,
    kind: line.kind,
    garment_title: line.garmentTitle.trim(),
    colour_label: line.colourLabel.trim(),
    sizes: line.sizes
      .map((size) => ({ size_label: size.sizeLabel, quantity: parseQuantity(size.quantity) }))
      .filter((size) => size.quantity > 0),
  };
}

export function buildManualOrderPayload(state: ManualOrderFormState): ManualOrderPayloadInput {
  return {
    customer: {
      name: state.customer.name.trim(),
      company: textOrNull(state.customer.company),
      email: textOrNull(state.customer.email),
      phone: textOrNull(state.customer.phone),
    },
    job: {
      name: state.job.name.trim(),
      po_number: textOrNull(state.job.poNumber),
      due_date: state.job.dueDate,
      priority: state.job.priority,
      hold_for_art_approval: state.job.holdForArtApproval,
    },
    delivery:
      state.delivery.mode === 'pickup'
        ? { mode: 'pickup' }
        : { mode: 'ship', address: buildAddress(state.delivery.address) },
    draft_id: null,
    lines: state.lines.map(buildLine),
  };
}

export function buildCreateInput(state: ManualOrderFormState, idempotencyKey: string): ManualOrderCreateInput {
  return {
    location_id: state.locationId ?? '',
    client_org_id: state.clientOrgId,
    idempotency_key: idempotencyKey,
    payload: buildManualOrderPayload(state),
  };
}

/**
 * Per-line blockers, in the order a coordinator would fix them (a line can
 * report more than one). A line whose floor offers no print method is
 * blocked for that line only (D-29). A from-stock card whose ticked colours
 * each print but share no method is blocked on its lead (`noSharedMethod`:
 * one method prints the whole card, D-4). A customer supplied or free form
 * line needs a front or back print (the server refuses one without).
 */
export function validateLines(lines: readonly ManualLineState[]): ManualLineIssue[] {
  const issues: ManualLineIssue[] = [];
  for (const card of groupLineCards(lines)) {
    const cardLines = card.kind === 'stock' ? card.lines : [card.line];
    for (const line of cardLines) {
      const add = (error: ManualLineIssue['error']) => issues.push({ lineId: line.id, error });
      if (line.kind === 'stock') {
        if (!line.product || line.colour === null) {
          add('noProduct');
          continue;
        }
      } else if (!line.garmentTitle.trim()) {
        add('noGarment');
      }
      if (linePieces(line) === 0) add('noPieces');
      if (line.printMethods.length === 0) add('noPrintMethod');
      if (line.kind !== 'stock' && line.spots.length === 0) add('noPrintSpot');
    }
    if (
      cardLines.length > 1 &&
      cardLines.every((line) => line.printMethods.length > 0) &&
      cardMethods(cardLines).length === 0
    ) {
      issues.push({ lineId: cardLines[0]!.id, error: 'noSharedMethod' });
    }
  }
  return issues;
}

/**
 * Everything that stops Create, as inline errors. Short stock is NOT here
 * (D-19: create anyway; the order waits in Out of stock). `today` is the
 * browser's local `YYYY-MM-DD`; the due date may be today or later.
 */
export function validateManualOrder(state: ManualOrderFormState, today: string): ManualFormErrors {
  const errors: ManualFormErrors = { fields: {}, lines: {} };
  if (!state.customer.name.trim()) errors.fields['customer.name'] = 'required';
  if (!state.job.name.trim()) errors.fields['job.name'] = 'required';
  if (!state.job.dueDate) errors.fields['job.dueDate'] = 'required';
  else if (state.job.dueDate < today) errors.fields['job.dueDate'] = 'dueDatePast';
  if (!state.locationId) errors.fields.locationId = 'required';
  if (state.delivery.mode === 'ship' && !state.delivery.address.address1.trim()) {
    errors.fields['delivery.address1'] = 'required';
  }
  if (state.lines.length === 0) errors.fields.lines = 'noLines';
  // One message per line: the first thing to fix.
  for (const issue of validateLines(state.lines)) errors.lines[issue.lineId] ??= issue.error;
  return errors;
}

/**
 * True when a line holds anything worth asking about before it is thrown away.
 * A print spot counts once it has a file (every new line starts with an
 * empty Front spot, which is not content).
 */
export function lineHasContent(line: ManualLineState): boolean {
  if (linePieces(line) > 0 || line.note.trim() !== '') return true;
  if (line.mockupUploadId !== null || line.spots.some((spot) => spot.uploadId !== null)) return true;
  if (line.kind === 'stock') return line.product !== null;
  return line.garmentTitle.trim() !== '' || line.colourLabel.trim() !== '';
}

/** The methods the order's lines will print with, in first-use order (the rail). */
export function usedPrintMethods(lines: readonly ManualLineState[]): ManualPrintMethod[] {
  const used = new Set<ManualPrintMethod>();
  for (const line of lines) {
    const method = lineMethod(line);
    if (method) used.add(method);
  }
  return [...used];
}

/** True once anything was typed or picked (drives the leave-page guard). */
export function isFormDirty(state: ManualOrderFormState): boolean {
  const { customer, job, delivery } = state;
  if (Object.values(customer).some((v) => v.trim() !== '')) return true;
  if (job.name.trim() || job.poNumber.trim() || job.dueDate || job.priority || job.holdForArtApproval) return true;
  if (Object.values(delivery.address).some((v) => v.trim() !== '')) return true;
  if (state.clientOrgId) return true;
  return state.lines.some(lineHasContent);
}

/** The browser's local calendar day as `YYYY-MM-DD`. */
export function localIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whole days from `today` to `due` (both local `YYYY-MM-DD`). */
export function daysUntil(today: string, due: string): number | null {
  const a = parseDateValue(today);
  const b = parseDateValue(due);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Print spot geometry (D-10, D-25): what the editor, the DPI pill and the
// reducer read from a spot's upload facts.
// ---------------------------------------------------------------------------

/** Width in pixels of what prints: the trimmed width, else the whole image. */
export function spotPrintWidthPx(upload: ManualSpotUploadMeta | null): number | null {
  if (!upload) return null;
  return upload.trimWidth ?? upload.widthPx;
}

/** Height / width of what prints (the trim box, else the image); 1 when unknown. */
export function spotAspect(upload: ManualSpotUploadMeta | null): number {
  if (!upload) return 1;
  const w = upload.trimWidth ?? upload.widthPx;
  const h = upload.trimHeight ?? upload.heightPx;
  return w && h && w > 0 && h > 0 ? h / w : 1;
}

/** The DPI a spot prints at with its current placement, or null with no file / placement. */
export function spotDpi(spot: Pick<ManualSpotState, 'upload' | 'placement'>): number | null {
  const px = spotPrintWidthPx(spot.upload);
  if (px === null || !spot.placement) return null;
  return placementDpi(px, spot.placement.width_in);
}
